import {
  PROFILE_FAV_FOOTBALL_TEAM_OPTIONS,
  favFootballLongLabel,
} from "../components/Profile/profileFavFootballTeams";
import { resolveFirstLoadingImageUrl } from "./resolveFirstLoadingImageUrl";
import { sportImagePublicBase } from "./sportImageCdn";

/** Crests at `Football/Team Logos/{slug}.png` (CloudFront → cricket8-web-content). */
const DEFAULT_FOOTBALL_TEAM_LOGO_S3_PREFIX = "Football/Team Logos";

export const FOOTBALL_TEAM_LOGO_S3_BASE = sportImagePublicBase(
  DEFAULT_FOOTBALL_TEAM_LOGO_S3_PREFIX,
  process.env.FOOTBALL_TEAM_LOGO_BASE
);

/** Default crest shields — `Football/Team Logos/default/` (same override rules as cricket IPL shields). */
export const FOOTBALL_TEAM_LOGO_DEFAULT_S3_FOLDER = "default";

/**
 * Default shield when no team crest is found — mirrors {@link CricketTeamLogo} fallbackOveride:
 * - `"false"` → `shield.png`
 * - `"white"` → `shield_white.png`
 * - any other truthy value → `shield_subtle_grey.png`
 */
export function resolveFootballDefaultShieldUrl(fallbackOverride: string = "false"): string {
  let file = "shield.png";
  if (fallbackOverride && fallbackOverride !== "false") {
    file = fallbackOverride === "white" ? "shield_white.png" : "shield_subtle_grey.png";
  }
  return `${FOOTBALL_TEAM_LOGO_S3_BASE}${FOOTBALL_TEAM_LOGO_DEFAULT_S3_FOLDER}/${file}`;
}

export function isFootballDefaultShieldUrl(src: unknown): boolean {
  if (typeof src !== "string" || !src) return false;
  try {
    const path = src.startsWith("/") ? src.split("?")[0] : new URL(src).pathname;
    return /\/default\/shield(?:_[\w]+)?\.png$/i.test(path);
  } catch {
    return /\/default\/shield(?:_[\w]+)?\.png$/i.test(src);
  }
}

const FOOTBALL_TEAM_NAME_ALIASES: Record<string, string> = {
  "man city": "manchester-city",
  "man utd": "manchester-united",
  "man united": "manchester-united",
  spurs: "tottenham-hotspur",
  tottenham: "tottenham-hotspur",
  "tottenham hotspur": "tottenham-hotspur",
  atletico: "atletico-madrid",
  "atletico madrid": "atletico-madrid",
  "ac milan": "milan",
  inter: "inter-milan",
  "inter milan": "inter-milan",
  bayern: "bayern-munich",
  "bayern munich": "bayern-munich",
  dortmund: "borussia-dortmund",
  "borussia dortmund": "borussia-dortmund",
  psg: "paris-saint-germain",
  "paris saint germain": "paris-saint-germain",
  arsenal: "arsenal",
  "arsenal fc": "arsenal",
  chelsea: "chelsea",
  "chelsea fc": "chelsea",
  liverpool: "liverpool",
  "liverpool fc": "liverpool",
  barcelona: "barcelona",
  "fc barcelona": "barcelona",
  "real madrid": "real-madrid",
  juventus: "juventus",
  "juventus fc": "juventus",
};

/** Strip youth / women / reserve suffixes so "Switzerland U19 W" → "Switzerland". */
const TEAM_LOGO_SUFFIX_PATTERNS = [
  /\s+u\d{1,2}\s+w$/i,
  /\s+u\d{1,2}$/i,
  /\s+w$/i,
  /\s+women$/i,
  /\s+ladies$/i,
  /\s+reserves?$/i,
  /\s+ii$/i,
  /\s+iii$/i,
  /\s+iv$/i,
  /\s+b$/i,
] as const;

/** Leading club abbreviations — try slug with and without (e.g. FK Kauno Žalgiris → kauno-zalgiris). */
const TEAM_LOGO_CLUB_PREFIX_PATTERN =
  /^(?:1\.?\s*)?(?:fk|fc|ac|afc|sc|cd|cf|ca|sv|bsc|tsv|fsv|vfb|vfl|as|ss|us|sd|ud|ec|cr|se|rb|rc|sk|nk|ifk|hsk|spvgg)\.?\s+/i;

export const stripClubPrefixFromTeamName = (name: string): string => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  const next = trimmed.replace(TEAM_LOGO_CLUB_PREFIX_PATTERN, "").trim();
  return next || trimmed;
};

const normalizeFootballTeamName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const slugifyFootballTeamName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export const stripTeamLogoSuffixes = (name: string): string => {
  let current = String(name || "").trim();
  if (!current) return "";

  let changed = true;
  while (changed) {
    changed = false;
    for (const pattern of TEAM_LOGO_SUFFIX_PATTERNS) {
      const next = current.replace(pattern, "").trim();
      if (next && next !== current) {
        current = next;
        changed = true;
      }
    }
  }
  return current;
};

/** Name variants to try when resolving an S3 slug (most specific first). */
export const teamNameVariantsForLogoLookup = (teamName: string): string[] => {
  const variants: string[] = [];
  const seen = new Set<string>();
  const addVariant = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    variants.push(trimmed);

    const withoutPrefix = stripClubPrefixFromTeamName(trimmed);
    if (withoutPrefix && withoutPrefix !== trimmed && !seen.has(withoutPrefix)) {
      seen.add(withoutPrefix);
      variants.push(withoutPrefix);
    }
  };

  addVariant(teamName);
  let stripped = teamName;
  while (true) {
    const next = stripTeamLogoSuffixes(stripped);
    if (!next || next === stripped) break;
    stripped = next;
    addVariant(stripped);
  }
  return variants;
};

const resolveSlugFromNameVariant = (name: string): string | null => {
  const norm = normalizeFootballTeamName(name);
  if (!norm) return null;

  const aliasSlug = FOOTBALL_TEAM_NAME_ALIASES[norm];
  if (aliasSlug) return aliasSlug;

  for (const opt of PROFILE_FAV_FOOTBALL_TEAM_OPTIONS) {
    const candidates = [opt.slug, opt.label, favFootballLongLabel(opt.slug)]
      .filter(Boolean)
      .map((c) => normalizeFootballTeamName(c));
    if (candidates.includes(norm)) return opt.slug;
  }

  return slugifyFootballTeamName(name) || null;
};

/** Ordered S3 slug candidates — fixture slug first, then alias variants. */
export const resolveFootballTeamLogoS3SlugCandidates = (teamName: string): string[] => {
  const slugs: string[] = [];
  const seen = new Set<string>();
  const add = (slug: string | null | undefined) => {
    const value = slug?.trim();
    if (value && !seen.has(value)) {
      seen.add(value);
      slugs.push(value);
    }
  };

  for (const variant of teamNameVariantsForLogoLookup(teamName)) {
    add(slugifyFootballTeamName(variant));
  }
  for (const variant of teamNameVariantsForLogoLookup(teamName)) {
    add(resolveSlugFromNameVariant(variant));
  }
  return slugs;
};

export function footballTeamLogoS3Url(slug: string): string {
  const safe = slugifyFootballTeamName(slug) || slug;
  return `${FOOTBALL_TEAM_LOGO_S3_BASE}${encodeURIComponent(safe)}.png`;
}

export function resolveFootballTeamLogoS3UrlsFromName(teamName: string): string[] {
  return resolveFootballTeamLogoS3SlugCandidates(teamName).map((slug) => footballTeamLogoS3Url(slug));
}

/**
 * First football team crest URL that loads successfully, or null (no default shield).
 * Mirrors resolveCricketTeamLogoUrl for My Games history cards.
 * Probes off-DOM so callers never paint a broken-image icon.
 */
export function resolveFootballTeamLogoUrl(teamName: string): Promise<string | null> {
  return resolveFirstLoadingImageUrl(resolveFootballTeamLogoS3UrlsFromName(teamName));
}

export function isGoalserveFootballLogo(value: unknown): value is string {
  if (typeof value !== "string" || !value) return false;
  return value.startsWith("data:image") || value.startsWith("/9j/") || value.startsWith("iVBOR");
}

/**
 * Resolve S3 crest URL from a display name (first candidate only).
 * Use resolveFootballTeamLogoS3UrlsFromName for full fallback chain.
 */
export function resolveFootballTeamLogoS3UrlFromName(teamName: string): string | null {
  const urls = resolveFootballTeamLogoS3UrlsFromName(teamName);
  return urls[0] ?? null;
}
